import React, { Fragment, useContext, useEffect, useState, useCallback, useMemo, useRef } from "react";
import { Formik, Form as FormikForm, Field, useFormikContext } from "formik";
import * as Yup from "yup";
import styled, { useTheme } from "styled-components";
import classNames from "classnames";
import moment from "moment";

// Import your existing components
import {
  SelectField,
  RadioField,
  LabelField,
  RichTextField,
  FileField2,
  FileField,
  DownloadField,
  PhoneField,
  StyledDate,
  NumberField,
  InputField,
  ServerPropField,
} from "./FormElements";
import TransformValue from "./TransformValue";
import { mapValidationSchema } from "../formValidation";
import { defaults } from "../mapInitialValues";
import { formatNumber } from "../util/formatter";
import { isValidValue } from "../util/generalUtil";
import { mapOperator } from "../util";
import AppContext from "../contexts/AppContext";
import appFetch from "../appFetch";
import apiEndpoints from "../apiEndpoints";

// Helper function to map field types
function mapField(type) {
  switch (type) {
    case "select":
    case "multiselect":
      return SelectField;
    case "radio":
      return RadioField;
    case "dropdown":
      return SelectField;
    case "label":
      return LabelField;
    case "richtext":
      return RichTextField;
    case "phonenumber":
      return PhoneField;
    case "date":
    case "datetime":
      return StyledDate;
    case "number":
      return NumberField;
    case "upload":
      return FileField2;
    case "download":
      return DownloadField;
    case "serverprop":
      return ServerPropField;
    default:
      return InputField;
  }
}

// Helper function to get initial values - memoized to prevent recalculation
const getInitialValues = (controls, lines) => {
  let values = {};
  lines?.forEach((row) => {
    controls?.forEach(({ name, type }) => {
      let newName = `${name}_line_${row["LINE"]}`;
      values[newName] = row[name?.toUpperCase()] ?? defaults(type);
      values[newName] = ["number", "money"].includes(type)
        ? parseFloat(values[newName])
        : values[newName];
    });
  });
  return values;
};

// Separate component to handle form context exposure safely
const FormContextBridge = ({ onFormContextReady }) => {
  const formikContext = useFormikContext();
  const previousContextRef = useRef();
  
  useEffect(() => {
    // Only call the callback if the context has actually changed in meaningful ways
    const currentContext = {
      values: formikContext.values,
      errors: formikContext.errors,
      touched: formikContext.touched,
      isValid: formikContext.isValid,
      dirty: formikContext.dirty,
    };
    
    const hasChanged = !previousContextRef.current || 
      JSON.stringify(previousContextRef.current) !== JSON.stringify(currentContext);
    
    if (hasChanged && onFormContextReady) {
      onFormContextReady({
        ...formikContext,
        ...currentContext
      });
      previousContextRef.current = currentContext;
    }
  }, [
    formikContext.values,
    formikContext.errors, 
    formikContext.touched,
    formikContext.isValid,
    formikContext.dirty,
    onFormContextReady
  ]);

  return null; // This component doesn't render anything
};

// Main TransactionLine component - rewritten for stability
const TransactionLine = ({
  // Data props
  header = [],
  lines = [],
  editable_controls = [],
  
  // Configuration props
  hasUpload = null,
  hasDownload = null,
  
  // Form submission props
  headerControl = [],
  item = {},
  
  // Layout props
  layoutid,
  target,
  targetid,
  
  // Form validation
  validationSchema = {},
  
  // Custom event handlers
  onPublishInputChange = null,
  onFormContextReady = null,
  
  // Render props for extensibility
  renderFooter = null,
  renderBeforeTable = null,
  renderAfterTable = null,
  renderWithForm = null,
  
  // Additional props passed through
  ...additionalProps
}) => {
  const appContext = useContext(AppContext);

  // Memoize filtered controls to prevent recalculation
  const _controls = useMemo(() => 
    editable_controls?.filter(row => !/download/i.test(row?.column)) || [],
    [editable_controls]
  );

  // Memoize initial values to prevent Formik re-initialization
  const initialValues = useMemo(() => 
    getInitialValues(_controls, lines),
    [_controls, lines]
  );

  // Memoize validation schema to prevent recreation
  const finalValidationSchema = useMemo(() => {
    if (Object.keys(validationSchema).length > 0) {
      return validationSchema;
    }

    const defaultValidationSchema = {};
    
    for (let control of _controls) {
      let { type, value, name, parent, defaultValue, validation } = control;

      if (/label/i.test(type)) {
        continue;
      }

      lines.forEach((m) => {
        let propName = `${name}_line_${m["LINE"]}`;

        if (validation?.lessThanOrEqual) {
          const lessThanOrEqual = `${control.validation.lessThanOrEqual}_line_${m["LINE"]}`;
          defaultValidationSchema[propName] = mapValidationSchema({
            ...control,
            name: propName,
            validation: { ...validation, lessThanOrEqual },
          });
          return;
        }

        if (validation?.lessThan) {
          const lessThan = `${control.validation.lessThan}_line_${m["LINE"]}`;
          defaultValidationSchema[propName] = mapValidationSchema({
            ...control,
            name: propName,
            validation: { ...validation, lessThan },
          });
          return;
        }

        if (validation?.greaterThanOrEqual) {
          const greaterThanOrEqual = `${control.validation.greaterThanOrEqual}_line_${m["LINE"]}`;
          defaultValidationSchema[propName] = mapValidationSchema({
            ...control,
            name: propName,
            validation: { ...validation, greaterThanOrEqual },
          });
          return;
        }

        if (validation?.greaterThan) {
          const greaterThan = `${control.validation.greaterThan}_line_${m["LINE"]}`;
          defaultValidationSchema[propName] = mapValidationSchema({
            ...control,
            name: propName,
            validation: { ...validation, greaterThan },
          });
          return;
        }

        defaultValidationSchema[propName] = mapValidationSchema({
          ...control,
          name: propName,
        });
      });
    }

    return defaultValidationSchema;
  }, [_controls, lines, validationSchema]);

  // Memoize Yup schema to prevent recreation
  const yupSchema = useMemo(() => 
    Yup.object(finalValidationSchema),
    [finalValidationSchema]
  );

  // Stable callback using useCallback
  const stableOnFormContextReady = useCallback((context) => {
    if (onFormContextReady) {
      onFormContextReady(context);
    }
  }, [onFormContextReady]);

  return (
    <>
      {renderBeforeTable && renderBeforeTable()}
      
      <Formik
        initialValues={initialValues}
        validationSchema={yupSchema}
        enableReinitialize={true}
      >
        <FormikForm>
          {/* Safe form context bridge */}
          {onFormContextReady && (
            <FormContextBridge onFormContextReady={stableOnFormContextReady} />
          )}
          
          <TransactionLineForm
            header={header}
            lines={lines}
            hasUpload={hasUpload}
            hasDownload={hasDownload}
            _controls={_controls}
            editable_controls={editable_controls}
            headerControl={headerControl}
            item={item}
            layoutid={layoutid}
            target={target}
            targetid={targetid}
            onPublishInputChange={onPublishInputChange}
            appContext={appContext}
            {...additionalProps}
          />
        </FormikForm>
      </Formik>
      
      {renderAfterTable && renderAfterTable()}
      {renderFooter && renderFooter()}
    </>
  );
};

// Internal form component - optimized to prevent re-renders
const TransactionLineForm = React.memo(({
  header,
  lines,
  hasUpload,
  hasDownload,
  _controls,
  editable_controls,
  headerControl,
  item,
  layoutid,
  target,
  targetid,
  onPublishInputChange,
  appContext,
  ...additionalProps
}) => {
  const formikContext = useFormikContext();
  const {
    values,
    handleChange,
    setFieldValue,
    setValues,
    setStatus,
    status,
    validateForm,
    setErrors,
    setTouched,
    submitForm,
  } = formikContext;

  const theme = useTheme();
  
  // Use refs for stable references to prevent re-renders
  const computeEventsRef = useRef({});
  const timeoutRef = useRef({});

  // Checkbox state for multi-select
  const [checkedLines, setCheckedLines] = useState(() => 
    new Array(lines.length).fill(false)
  );
  const [lastCheckIndex, setLastCheckIndex] = useState(null);

  // Memoize compute events setup to run only when controls or lines change
  const computeEvents = useMemo(() => {
    const events = {};
    
    for (let control of _controls) {
      let {
        validation,
        type,
        name,
        compute,
        options,
        server_fields,
        script,
        script_field,
        serverControl,
      } = control;

      // Script field handling
      if (script_field) {
        lines.forEach((m, idx) => {
          let _name = `${name}_line_${m["LINE"]}`;
          events[_name] = events[_name] || [];
          
          const func = (e) => {
            let { value: fieldValue, name: fieldName } = e.target;
            let line = fieldName.match(/_line.+/)[0];
            let scriptOptions = options || [];
            let scriptProps =
              {}.constructor == fieldValue?.constructor
                ? fieldValue
                : scriptOptions.find((m) => m.value === fieldValue);

            if (script) {
              scriptProps = scriptProps || { script };
            }

            if (!scriptProps) return;

            let scriptArr = scriptProps.script;
            let newStatus = {};

            scriptArr.forEach((sc) => {
              let { field, ...rest } = sc;
              newStatus[`${field}${line}`] = rest;
            });

            let statusMod = { ...(status || {}), ...newStatus };

            if (Object.keys(statusMod).length > 0) {
              setStatus(statusMod);
            }
          };
          
          events[_name].onChange = (events[_name]?.onChange ?? []).concat(func);
        });
      }

      // Server property handling
      if (type === "serverprop" && server_fields) {
        lines.forEach((m, idx) => {
          let _name = `${name}_line_${m["LINE"]}`;
          let _server_fields = [...server_fields].map(
            (n) => `${n}_line_${m["LINE"]}`
          );

          for (let field of _server_fields) {
            const func = (e) => {
              let { value: fieldValue, name: fieldName } = e.target;
              let currVals = {};

              fieldValue = moment.isDate(fieldValue)
                ? moment(fieldValue).format("YYYY-MM-DD")
                : fieldValue;

              currVals[fieldName.replace(/_line_\d/, "")] =
                fieldValue?.value ?? fieldValue;
              
              _server_fields.forEach((f) => {
                if (f === fieldName) return;
                let value = values[f];
                value = moment.isDate(value)
                  ? moment(value).format("YYYY-MM-DD")
                  : value;
                currVals[f.replace(/_line_\d/, "")] = value?.value ?? value;
              });

              if (Object.values(currVals ?? {}).some((x) => !isValidValue(x))) {
                return;
              }

              const handler = () => {
                setFieldValue(_name, "loading...");
                let payload = {
                  jsonString: JSON.stringify([currVals]),
                  targetId: control?.target_id?.toString(),
                  target: type,
                  applicationId: appContext?.menuId?.toString(),
                  parentDropValue: null,
                };

                appFetch
                  .post(apiEndpoints.getServerProps, {
                    body: JSON.stringify(payload),
                  })
                  .then((res) => {
                    setFieldValue(_name, res);
                  })
                  .catch((e) => {
                    setFieldValue(_name, "");
                  });
              };

              if (timeoutRef.current[fieldName]) {
                clearTimeout(timeoutRef.current[fieldName]);
              }
              timeoutRef.current[fieldName] = setTimeout(handler, 500);
            };

            if (!events[field]) {
              events[field] = {};
            }

            events[field].onChange = (events[field]?.onChange ?? []).concat(func);
          }
        });
      }

      // Compute field handling
      if (compute instanceof Object) {
        let { operator, withField } = compute;
        let reduceFunc = mapOperator(operator);

        lines.forEach((m, idx) => {
          let withFields = withField.map((n) => `${n}_line_${m["LINE"]}`);

          for (let field of withFields) {
            const func = (e) => {
              let { value: fieldValue, name: fieldName } = e.target;
              let _name = `${name}_line_${m["LINE"]}`;

              let computeValues = withFields.map((f) => {
                if (f === fieldName) {
                  return parseFloat(fieldValue?.replace(/\,/g, ""));
                }
                return parseFloat(values[f]?.toString()?.replace(/\,/g, ""));
              });

              let result = computeValues.reduce(reduceFunc);
              setFieldValue(_name, formatNumber(result) || "");
            };

            if (!events[field]) {
              events[field] = {};
            }

            events[field].onChange = (events[field]?.onChange ?? []).concat(func);
          }
        });
        control.disabled = true;
      }
    }

    computeEventsRef.current = events;
    return events;
  }, [_controls, lines, status, values, setFieldValue, setStatus, appContext]);

  // Stable toggle check function
  const toggleCheck = useCallback(({ nativeEvent: { shiftKey: shiftKeyDown } }, lineNo) => {
    setCheckedLines(prevCheckedLines => {
      const newState = [...prevCheckedLines];
      const lineChecked = newState[lineNo];

      if (lastCheckIndex !== null && shiftKeyDown) {
        const direction = lastCheckIndex < lineNo ? 1 : -1;
        const diff = Math.abs(lastCheckIndex - lineNo);
        let movement = lastCheckIndex;

        for (var i = 0; i <= diff; i++) {
          newState[movement] = !lineChecked;
          movement += direction;
        }
      } else {
        newState[lineNo] = !newState[lineNo];
      }

      return newState;
    });
    setLastCheckIndex(lineNo);
  }, [lastCheckIndex]);

  // Stable publish input change function
  const publishInputChange = useCallback((fieldName, newValue) => {
    if (onPublishInputChange) {
      onPublishInputChange(fieldName, newValue);
      return;
    }

    let newFieldName = fieldName.replace(/_line_\d+/, "");
    let newFields = {};

    let lineNo = fieldName.match(/\d+/)?.[0] ?? 0;
    if (checkedLines[lineNo - 1]) {
      checkedLines.forEach((isChecked, lineIdx) => {
        if (isChecked) {
          let field = `${newFieldName}_line_${lineIdx + 1}`;
          newFields[field] = newValue;
        }
      });
      setValues({ ...values, ...newFields });
    } else {
      newFields[fieldName] = newValue;
      setValues({ ...values, ...newFields });
    }
  }, [onPublishInputChange, checkedLines, values, setValues]);

  // Cleanup timeouts on unmount
  useEffect(() => {
    return () => {
      Object.values(timeoutRef.current).forEach(timeout => {
        if (timeout) clearTimeout(timeout);
      });
    };
  }, []);

  return (
    <div className="overflow-x-visible w-full tranx-table scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-gray-300">
      <table className="min-w-full leading-normal">
        <thead>
          <tr>
            {header.map((str, idx) => {
              let show = !str.endsWith("_H");
              return (
                show && (
                  <th
                    key={idx}
                    className="px-5 py-3 border-b-2 border-gray-200 bg-gray-100 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider"
                  >
                    {str.replace(/_/g, " ")}
                  </th>
                )
              );
            })}
          </tr>
        </thead>
        <tbody>
          {lines.map((row, rowIdx) => (
            <React.Fragment key={row.LINE || rowIdx}>
              <tr
                className={classNames("border-gray-300", {
                  "border-b": rowIdx + 1 !== lines.length,
                  "bg-gray-100": checkedLines[rowIdx],
                })}
              >
                {header
                  ?.filter((str) => !str.endsWith("_H"))
                  ?.map((str, tdIndex) => {
                    const editableControl = editable_controls?.find(
                      (c) => c.column === str
                    );
                    let { column, name, parent, ...rest } = editableControl || {};

                    if (name) {
                      name = `${name}_line_${row["LINE"]}`;
                    }
                    if (parent) {
                      parent = `${parent}_line_${row["LINE"]}`;
                    }

                    let value = row[str.toUpperCase()];
                    let events = computeEvents[name] ?? {};
                    let eventHandlers = {};
                    
                    Object.keys(events).forEach((eventType) => {
                      eventHandlers[eventType] = (e) => {
                        events[eventType].forEach((func) => {
                          func(e);
                        });
                      };
                    });

                    return (
                      <td key={`${str}-${tdIndex}`} className="px-5 py-2 border-gray-200 text-sm">
                        {tdIndex === 0 && (
                          <div className="flex items-center">
                            {lines.length > 1 && (
                              <div className="mb-[0.125rem] mr-[0.25rem] block min-h-[1.5rem] pl-[1.5rem]">
                                <input
                                  className="relative float-left -ml-[1.5rem] mr-[6px] mt-[0.15rem] h-[1.125rem] w-[1.125rem] appearance-none rounded-[0.25rem] border-[0.125rem] border-solid border-neutral-300 outline-none before:pointer-events-none before:absolute before:h-[0.875rem] before:w-[0.875rem] before:scale-0 before:rounded-full before:bg-transparent before:opacity-0 before:shadow-[0px_0px_0px_13px_transparent] before:content-[''] checked:border-primary checked:bg-primary checked:before:opacity-[0.16] checked:after:absolute checked:after:-mt-px checked:after:ml-[0.25rem] checked:after:block checked:after:h-[0.8125rem] checked:after:w-[0.375rem] checked:after:rotate-45 checked:after:border-[0.125rem] checked:after:border-l-0 checked:after:border-t-0 checked:after:border-solid checked:after:border-white checked:after:bg-transparent checked:after:content-[''] hover:cursor-pointer hover:before:opacity-[0.04] hover:before:shadow-[0px_0px_0px_13px_rgba(0,0,0,0.6)] focus:shadow-none focus:transition-[border-color_0.2s] focus:before:scale-100 focus:before:opacity-[0.12] focus:before:shadow-[0px_0px_0px_13px_rgba(0,0,0,0.6)] focus:before:transition-[box-shadow_0.2s,transform_0.2s] focus:after:absolute focus:after:z-[1] focus:after:block focus:after:h-[0.875rem] focus:after:w-[0.875rem] focus:after:rounded-[0.125rem] focus:after:content-[''] checked:focus:before:scale-100 checked:focus:before:shadow-[0px_0px_0px_13px_var(--primary)] checked:focus:before:transition-[box-shadow_0.2s,transform_0.2s] checked:focus:after:-mt-px checked:focus:after:ml-[0.25rem] checked:focus:after:h-[0.8125rem] checked:focus:after:w-[0.375rem] checked:focus:after:rotate-45 checked:focus:after:rounded-none checked:focus:after:border-[0.125rem] checked:focus:after:border-l-0 checked:focus:after:border-t-0 checked:focus:after:border-solid checked:focus:after:border-white checked:focus:after:bg-transparent dark:border-neutral-600 dark:checked:border-primary dark:checked:bg-primary dark:focus:before:shadow-[0px_0px_0px_13px_rgba(255,255,255,0.4)] dark:checked:focus:before:shadow-[0px_0px_0px_13px_var(--primary)]"
                                  type="checkbox"
                                  value=""
                                  onChange={(e) => toggleCheck(e, rowIdx)}
                                  checked={checkedLines[rowIdx]}
                                />
                              </div>
                            )}
                            <p
                              className={classNames(
                                "text-gray-900 whitespace-no-wrap text-xs",
                                {
                                  "text-primary underline":
                                    str.toLowerCase() === "item",
                                }
                              )}
                            >
                              <TransformValue value={value} />
                            </p>
                          </div>
                        )}
                        {tdIndex !== 0 && (
                          <>
                            {editableControl ? (
                              <div>
                                {React.createElement(
                                  mapField(editableControl.type),
                                  {
                                    name,
                                    parent,
                                    status,
                                    value,
                                    useVariableWidth: true,
                                    ...eventHandlers,
                                    publishInputChange,
                                    ...rest,
                                  }
                                )}
                              </div>
                            ) : (
                              <p
                                className={classNames(
                                  "text-gray-900 whitespace-no-wrap text-xs",
                                  {
                                    "text-primary underline":
                                      str.toLowerCase() === "item",
                                  }
                                )}
                              >
                                <TransformValue value={value} />
                              </p>
                            )}
                          </>
                        )}
                      </td>
                    );
                  })}
              </tr>
              {hasUpload && row["UPLOAD"] === "Yes" && (
                <tr>
                  <td colSpan={header?.filter(str => !str.endsWith("_H")).length}>
                    <div className="bg-gray-100 px-6 py-3">
                      <FileField
                        name={`${hasUpload.name}_line_${row["LINE"]}`}
                        label="Upload..."
                      />
                    </div>
                  </td>
                </tr>
              )}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
});

// Add display name for debugging
TransactionLineForm.displayName = 'TransactionLineForm';

export default TransactionLine;
export { getInitialValues };